import React, { useEffect, useRef, useState } from "react";
import IconButton from "@mui/material/IconButton";
import Slider from "@mui/material/Slider";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import StopIcon from "@mui/icons-material/Stop";
import DownloadIcon from "@mui/icons-material/Download";
import { useTranslation } from "../i18n/LocaleContext";
import { describePabxUrl, pabxFetch } from "../utils/pabxApi";

interface CallRecordingPlayerProps {
  url: string;
  fileName: string;
}

function formatTime(totalSeconds: number) {
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return "0:00";
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.floor(totalSeconds % 60);
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

const CallRecordingPlayer: React.FC<CallRecordingPlayerProps> = ({ url, fileName }) => {
  const { t } = useTranslation();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => setDuration(audio.duration || 0);
    const handleTimeUpdate = () => setCurrentTime(audio.currentTime || 0);
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };
    // O <audio> não depende de CORS (sem crossOrigin), então aqui a falha é de outra natureza:
    // arquivo ausente, rede, ou formato que o WebView não decodifica. MediaError.code:
    // 1 aborted, 2 network, 3 decode, 4 src not supported.
    const handleError = () => {
      const mediaError = audio.error;
      console.warn(
        `[pabx] recordingPlayback ${describePabxUrl(audio.currentSrc)} ` +
          `MediaError code=${mediaError?.code ?? "?"} ${mediaError?.message || ""}`.trim()
      );
    };

    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("error", handleError);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("error", handleError);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("ended", handleEnded);
    };
  }, []);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      void audio.play();
    } else {
      audio.pause();
    }
  };

  const handleStop = () => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    audio.currentTime = 0;
    setCurrentTime(0);
  };

  const handleSeek = (_event: Event, value: number | number[]) => {
    const audio = audioRef.current;
    if (!audio) return;
    const next = Array.isArray(value) ? value[0] : value;
    audio.currentTime = next;
    setCurrentTime(next);
  };

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const response = await pabxFetch("recordingDownload", url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      // Fallback (ex.: CORS bloqueado pelo PABX): abre a gravação em nova aba.
      window.open(url, "_blank", "noopener");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="call-recording-player">
      <audio ref={audioRef} src={url} preload="metadata" />
      <IconButton
        size="small"
        onClick={togglePlay}
        aria-label={isPlaying ? t("callRecordingPlayer.pause") : t("callRecordingPlayer.play")}
        className="call-recording-player-btn"
      >
        {isPlaying ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
      </IconButton>
      <IconButton
        size="small"
        onClick={handleStop}
        aria-label={t("callRecordingPlayer.stop")}
        className="call-recording-player-btn"
      >
        <StopIcon fontSize="small" />
      </IconButton>
      <Slider
        size="small"
        min={0}
        max={duration || 0}
        value={Math.min(currentTime, duration || 0)}
        onChange={handleSeek}
        aria-label={t("callRecordingPlayer.seek")}
        disabled={!duration}
        className="call-recording-player-slider"
      />
      <span className="call-recording-player-time">
        {formatTime(currentTime)} / {formatTime(duration)}
      </span>
      <IconButton
        size="small"
        onClick={handleDownload}
        disabled={downloading}
        aria-label={t("callRecordingPlayer.download")}
        className="call-recording-player-btn"
      >
        <DownloadIcon fontSize="small" />
      </IconButton>
    </div>
  );
};

export default CallRecordingPlayer;
